import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  Timestamp,
  where
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import moment from "moment";
import { useReactToPrint } from "react-to-print";

import Menu from "../../panel/Menu";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack
} from "@mui/material";

function Bookingtypewise() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000
  });

  const printRef = useRef();

  const [propertyId, setPropertyId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [bookType, setBookType] = useState("");
  const [bookingTypes, setBookingTypes] = useState([]);
  const [bookingData, setBookingData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  /* 🔐 AUTH + PROPERTY */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;

      const roleSnap = await getDoc(doc(db, "authroles", user.uid));
      if (roleSnap.exists()) {
        const pid = roleSnap.data().propertyId;
        setPropertyId(pid);
        fetchBookingTypes(pid);
      } else {
        Toast.fire({ icon: "error", title: "Property not assigned" });
      }
    });

    return () => unsub();
  }, []);

  /* 📌 FETCH BOOKING TYPES */
  const fetchBookingTypes = async (pid) => {
    const snap = await getDocs(
      query(collection(db, "bookingtypes"), where("propertyId", "==", pid))
    );

    setBookingTypes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  /* 📊 GET REPORT */
  const getReport = async () => {
    if (!fromDate || !toDate || !bookType) {
      Toast.fire({ icon: "error", title: "Select Booking Type & Dates" });
      return;
    }

    setLoading(false);
    setBookingData([]);
    setTotal(0);

    const start = new Date(fromDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(toDate);
    end.setDate(end.getDate() + 1);

    try {
      const q = query(
        collection(db, "reservations"),
        where("propertyId", "==", propertyId),
        where("bookingsource", "==", bookType),
        where("createdAt", ">=", Timestamp.fromDate(start)),
        where("createdAt", "<", Timestamp.fromDate(end))
      );

      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      const sum = data.reduce((s, b) => s + (b.totalamount || 0), 0);

      setBookingData(data);
      setTotal(sum.toFixed(2));
      setLoading(true);

    } catch (err) {
      console.error(err);
      Toast.fire({ icon: "error", title: "Error loading report" });
    }
  };

  /* 🖨 PRINT */
  const handlePrint = useReactToPrint({
    content: () => printRef.current
  });

  return (
    <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
      <Menu />

      <Box  className="content-wrapper" flex={1} p={3}>
        <Typography variant="h6" fontWeight={700} mb={2}>
          || Booking Type Wise Report ||
        </Typography>

        {/* FILTER CARD */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid className="row" spacing={2}>
              <Grid className="col-sm-3" sm={3}>
                <TextField
                  select
                  label="Booking Type"
                  value={bookType}
                  onChange={(e) => setBookType(e.target.value)}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">Select</MenuItem>
                  {bookingTypes.map(bt => (
                    <MenuItem key={bt.id} value={bt.bookingtype}>
                      {bt.bookingtype}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid className="col-sm-3" sm={3}>
                <TextField
                  type="date"
                  label="From Date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid className="col-sm-3" sm={3}>
                <TextField
                  type="date"
                  label="To Date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={3} alignSelf="center">
                <Button variant="contained" onClick={getReport}>
                  Get Report
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* REPORT */}
        {loading && (
          <>
            <Card ref={printRef}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" mb={2}>
                  <Typography>
                    From : {moment(fromDate).format("DD/MM/YYYY")}
                  </Typography>
                  <Typography fontWeight={700}>
                    {bookType} REPORT
                  </Typography>
                  <Typography>
                    To : {moment(toDate).format("DD/MM/YYYY")}
                  </Typography>
                </Stack>

                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Mobile</TableCell>
                        <TableCell>Check-In</TableCell>
                        <TableCell>Check-Out</TableCell>
                        <TableCell>Rooms</TableCell>
                        <TableCell>Adults</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Paid</TableCell>
                        <TableCell align="right">Remaining</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {bookingData.map((b, i) => (
                        <TableRow key={b.id}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>{b.customers?.custname}</TableCell>
                          <TableCell>{b.customers?.mobile}</TableCell>
                          <TableCell>{moment(b.checkindate).format("DD/MM/YYYY")}</TableCell>
                          <TableCell>{moment(b.checkoutdate).format("DD/MM/YYYY")}</TableCell>
                          <TableCell>
                            {b.rooms?.map((r, j) => (
                              <span key={j}>{r.roomno}, </span>
                            ))}
                          </TableCell>
                          <TableCell>{b.adults}</TableCell>
                          <TableCell>{b.bookingsource}</TableCell>
                          <TableCell align="right">{b.totalamount}</TableCell>
                          <TableCell align="right">{b.advance}</TableCell>
                          <TableCell align="right">{b.remaining}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography mt={2} fontWeight={700} bgcolor="#E0FCFE" p={1}>
                  Total Booking Amount : ₹ {total}
                </Typography>
              </CardContent>
            </Card>

            <Box textAlign="center" mt={3}>
              <Button variant="contained" color="success" onClick={handlePrint}>
                Print Report
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

export default Bookingtypewise;
